/**
 * Expandable level list for a SIM with assignment status and take-SIM entry points.
 *
 * @module components/sim/Levels
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React, {useEffect, useState} from "react";
import PropTypes from "prop-types";
import {
    Box,
    Button,
    Paper,
    Popper,
    Divider,
    Tooltip,
    Modal,
} from "@material-ui/core";
import {connect} from "react-redux";
import {addAssignmentStatus, fetchUserResponse} from "../../redux/sims/actions";
import StyledLink from "../common/StyledLink";
import {
    checkUserResultExist,
    checkContinueLevel,
} from "../../services/result.service";
import {useLocation, useHistory} from "react-router-dom";
import {useStyles} from "./Levels.js";
import Checkbox from '@material-ui/core/Checkbox';
import CircleUnchecked from '@material-ui/icons/RadioButtonUnchecked';
import CircleCheckedFilled from '@material-ui/icons/CheckCircle';
import {userFinalScoreResponse} from "../../redux/sims/actions";
import ScreenShareIcon from "@material-ui/icons/ScreenShare";
import VideocamIcon from "@material-ui/icons/Videocam";
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import {combinedElapsedSeconds} from "../../utils/time";

/**
 * The `Levels` variable is a functional component responsible for managing
 * and rendering the simulation levels, controls, and interactions within an
 * educational or training application.
 *
 * @param {Object} params - The parameters passed to the component.
 * @param {Object} params.level - Contains information about the current simulation level.
 * @param {number} params.index - The index of the current level in the sequence.
 * @param {string} params.simId - Identifier for the simulation instance.
 * @param {Function} params.fetchUserResponse - Function to fetch user interactions or progress.
 * @param {Object} params.assignmentData - Data related to a specific assignment or session.
 * @param {string} params.type - Indicates the simulation type.
 * @param {Function} params.addAssignmentStatus - Function to update the status of an assignment.
 * @param {string} params.currentAssignmentStatus - Tracks the current status of the assignment.
 * @param {Object} params.document - Document object, often used for referencing DOM-related actions.
 * @param {Object} params.hotLinks - Configurable links or resources related to the assignment.
 * @param {string} params.captivate_learning_url - URL for related learning materials or resources.
 * @param {string} params.captivate_learning_video_url - URL for relevant instructional videos.
 * @param {number} params.elapsedTime - Tracks the total elapsed time for the user during the simulation.
 * @param {Object} params.caseValuesArray - Object containing case-specific data for deeper simulation integration.
 * @param {Object} params.userResponse - Stores user responses at various points of the simulation.
 * @param {Function} params.userFinalScoreResponse - Function to send the user's final score and exit response.
 */
const Levels = ({
    level,
    index,
    simId,
    fetchUserResponse,
    assignmentData,
    type,
    addAssignmentStatus,
    currentAssignmentStatus,
    document,
    hotLinks,
    captivate_learning_url,
    captivate_learning_video_url,
    elapsedTime,
    caseValuesArray,
    userResponse,
    userFinalScoreResponse,
}) => {
    const PLAYMODE_PRACTICE = 1,
        PLAYMODE_AUTOPLAY = 2;

    const classes = useStyles();
    const [anchorEl, setAnchorEl] = useState(null);
    const [arrowRef, setArrowRef] = useState(null);
    const [userResultExist, setUserResultExist] = useState(null);
    const [openModal, setOpenModal] = useState(false);
    const [simType, setSimType] = useState(type);
    const [mode, setMode] = useState("practice");
    const [activeClip, setActiveClip] = useState(0);
    const history = useHistory();
    const [exited, setExited] = useState(0);
    const location = useLocation();
    const urlParams = new URLSearchParams(location.search);
    const open = Boolean(anchorEl);
    const id = open ? "popper with arrow" : undefined;
    const levelTitles = [
        'Watch and learn.Gets tips and training as an expert agent navigate the call you will only watch and learn in this interaction.',
        '',
        "Same call-now it's your turn! You will speak and read using a full script displayed on the screen",
        "Same call-now it's your turn! You will read and speak using keywords displayed on the screen as a guide",
        '',
        "Same call-now it's your turn! The call automatically plays through from start to finish.This means no stopping and no re-recording your audio.It's as close as it gets to real call experience !"
    ];

    useEffect(() => {
        if (Boolean(urlParams.get("exit")) && elapsedTime && exited === 0) {
            let lapsedTime = elapsedTime,
                totalSeconds = 0;
            if (userResponse.resumeLevel && userResponse.elapsedTime) {
                totalSeconds = combinedElapsedSeconds(lapsedTime, userResponse.elapsedTime);
                lapsedTime = totalSeconds;
            }
            const payload = {
                ...caseValuesArray,
                isPost: true,
                assignment_id: userResponse.assignment_id,
                elapsedTime: lapsedTime,
                totalSeconds: totalSeconds,
                exit_or_restart: true,
            };
            setExited(exited + 1);
            userFinalScoreResponse(userResponse._id, payload);
        }
    }, [elapsedTime, urlParams, caseValuesArray, userFinalScoreResponse, userResponse, exited]);

    useEffect(() => {
        /**
         * Executes an asynchronous function to prepare a payload and check if a user's result exists.
         *
         * This function creates a payload object using `simId`, `simLevelId`, and optionally an `assignmentId`,
         * based on the provided `assignmentData` or `simId` and `level` inputs. It then determines the `userId`
         * from `assignmentData` and calls `checkUserResultExist` with the payload and userId to determine
         * if a result exists for the user, updating the state variable `userResultExist`.
         *
         * @returns {Promise<void>} Resolves once the operation is completed and state is updated.
         */
        const asyncFunctionCall = async () => {
            const payload = {
                simId: assignmentData?.sim_id ? assignmentData.sim_id : simId,
                simLevelId: level.level_id,
                ...(assignmentData?.assignment_id
                    ? {assignmentId: assignmentData.assignment_id}
                    : {}),
            };
            const userId =
                assignmentData?.assigned_to_id || assignmentData?.agent_user_id;
            const exist = await checkUserResultExist(payload, userId);
            console.log('Checking results for level: %o', payload);
            console.log('Result = %o', exist);
            setUserResultExist(exist);
        };

        asyncFunctionCall().then(() => {
            assignmentData && assignmentData.sim_type
                ? setSimType(assignmentData.sim_type)
                : setSimType(type);
        });
    }, [assignmentData, level, simId, type]);

    useEffect(() => {
        const asyncFunctionCall = async () => {
            const payload = {
                simId: assignmentData?.sim_id ? assignmentData.sim_id : simId,
                simLevelId: level.level_id,
                ...(assignmentData?.assignment_id
                    ? {assignmentId: assignmentData.assignment_id}
                    : {}),
            };
            const userId =
                assignmentData?.assigned_to_id || assignmentData?.agent_user_id;
            const exist = await checkUserResultExist(payload, userId);
            setUserResultExist(exist);
        };

        asyncFunctionCall().then(() => {
            assignmentData && assignmentData.sim_type
                ? setSimType(assignmentData.sim_type)
                : setSimType(type);
        });
    }, [assignmentData, level, simId, type]);

    /**
     * Event handler function for handling click events.
     *
     * This function is triggered when a click event occurs and is used
     * to update the state by setting the current target element of the event.
     *
     * @param {Object} event - The event object associated with the click event.
     *                         It contains metadata about the event and the element
     *                         that triggered it.
     */
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    /**
     * Handles the closing of a popover by resetting the anchor element's reference.
     * Sets the anchor element to null, effectively closing any open popover UI component.
     */
    const handlePopoverClose = () => {
        setAnchorEl(null);
    };

    /**
     * A callback function to handle the assignment of the arrow element reference in a popper or tooltip-like component.
     * Typically used to set the arrow element for proper positioning and alignment.
     *
     * @param {HTMLElement|null} node - The DOM node representing the arrow element, or null if the reference is being cleared.
     */
    const handleArrowRef = (node) => {
        setArrowRef(node);
    };

    // Close Modal
    /**
     * A function that handles the closing of a modal by updating the state.
     *
     * This function sets the internal state to false, effectively indicating
     * that the modal should no longer be visible.
     */
    const handleClose = () => setOpenModal(false);

    /**
     * Initiates the practice mode for the application.
     *
     * The `startPractice` function sets the application mode to "practice" and verifies
     * if the user can continue progressing in their current level via the `checkContinueLevel` API.
     *
     * - If the user can continue (determined by `res.data.continue_agent`), it opens a modal
     *   and sets the active clip to the specified index.
     * - If continuation is not possible, the function checks the assignment's current status.
     *   If the assignment is incomplete, it updates the status to "IN PROGRESS" using
     *   the `addAssignmentStatus` function and restarts the session in "practice" mode.
     */
    const startPractice = () => {
        setMode("practice");
        checkContinueLevel(simId, level.level_id, "practice")
            .then((res) => {
                if (res.data.continue_agent) {
                    setOpenModal(true);
                    setActiveClip(res.data.active_clip_index);
                } else {
                    if (
                        assignmentData &&
                        assignmentData.assignment_id &&
                        assignmentData.status &&
                        (assignmentData.status.toLowerCase() !== 'completed')
                    ) {
                        const data = {
                            _id: assignmentData.assignment_id,
                            status: "IN PROGRESS",
                        };
                        addAssignmentStatus(data);
                    }
                    startOver("practice");
                }
            });
    };

    /**
     * Initiates the autoplay mode for the current context.
     * This function sets the overall mode to "autoplay", checks the continuation
     * level for the simulation ID and level ID, and proceeds based on the response.
     *
     * Behavior:
     * 1. If the `continue_agent` flag is true in the response, opens a modal and
     *    sets the active clip index.
     * 2. If the `continue_agent` flag is false, checks the assignment status.
     *    - If the assignment is not completed, it updates the assignment status
     *      to "IN PROGRESS".
     *    - Starts over in autoplay mode.
     *
     * Dependencies:
     * - Relies on functions `setMode`, `checkContinueLevel`, `setOpenModal`,
     *   `setActiveClip`, `addAssignmentStatus`, and `startOver` to perform
     *   respective actions.
     * - Utilizes data objects such as `simId`, `level`, and `assignmentData`
     *   to determine the current context and handle updates.
     */
    const startAutoPlay = () => {
        setMode("autoplay");
        checkContinueLevel(simId, level.level_id, "autoplay")
            .then((res) => {
                if (res.data?.continue_agent) {
                    setOpenModal(true);
                    setActiveClip(res.data.active_clip_index);
                } else {
                    if (
                        //assignmentData &&
                        assignmentData?.assignment_id &&
                        //assignmentData.status &&
                        (assignmentData?.status?.toLowerCase() !== 'completed')
                    ) {
                        const data = {
                            _id: assignmentData.assignment_id,
                            status: "IN PROGRESS",
                        };
                        addAssignmentStatus(data);
                    }
                    startOver("autoplay");
                }
            });
    };

    /**
     * Restarts the simulation with the specified simulation mode.
     *
     * @param {string} simMode - The mode in which the simulation should be restarted. If not provided, defaults to the current mode.
     *
     * This function performs the following steps:
     * 1. Dispatches a user response containing the simulation ID, level ID, mode, and optional assignment ID.
     * 2. Updates the assignment status to "IN PROGRESS" if an active assignment exists and its status is not "completed".
     * 3. Redirects to the simulation-taking page (`/sims/takesim`) with appropriate query parameters (e.g., level ID, assignment ID, order, reviewer status), while also passing state data like assignment details, simulation type, documents, and other data relevant to the simulation.
     */
    const startOver = (simMode) => {
        fetchUserResponse({
            sim_level_id: level.level_id,
            sim_id: simId,
            mode: simMode ? simMode : mode,
            resumeLevel: false,
            assignment_id: assignmentData ? assignmentData.assignment_id : undefined,
        });

        if (
            //assignmentData &&
            assignmentData?.assignment_id &&
            //assignmentData.status &&
            (assignmentData?.status?.toLowerCase() !== 'completed')
        ) {
            const data = {
                _id: assignmentData.assignment_id,
                status: "IN PROGRESS",
            };
            addAssignmentStatus(data);
        }

        history.push({
            pathname: "/sims/takesim",
            search: assignmentData?.assignment_id
                ? `?levelId=${level.level_id}&assignment_id=${
                    assignmentData.assignment_id
                }${
                    assignmentData?.reviewer ? "&reviewer=true" : "&reviewer=false"
                }&continueSimLevel=false&order=${level.level_order}`
                : `?levelId=${level.level_id}${
                    assignmentData?.reviewer ? "&reviewer=true" : "&reviewer=false"
                }&continueSimLevel=false&order=${level.level_order}`,
            state: {
                viewResult: false,
                assignment: assignmentData ? assignmentData : {},
                type: simType,
                documents: document,
                hotLinks: hotLinks,
                screenRecording: level.screenRecording,
                captivate_learning_video_url: captivate_learning_video_url,
                captivate_learning_url: captivate_learning_url,
            },
        });
    };

    /**
     * Resumes a simulation level by performing a sequence of actions, including sending user response data to the server,
     * updating the assignment status if applicable, and navigating to the appropriate simulation view.
     *
     * This function is used as part of the simulation navigation flow to allow users to continue a simulation from
     * a specific level. It handles state updates, conditional assignment details, and navigation with query parameters.
     *
     * Actions performed:
     * - Sends user response data, including the simulation level ID, simulation ID, mode, and additional assignment details.
     * - Updates the assignment status to "IN PROGRESS" if the assignment is not completed.
     * - Redirects the user to the simulation interface with the required query parameters for resuming the level.
     *
     * Variables considered:
     * - level.level_id: ID of the current level to resume.
     * - simId: ID of the simulation.
     * - mode: The mode of the simulation.
     * - assignmentData: Assignment-related data, including assignment ID and status.
     * - activeClip: The active clip index for the simulation.
     * - level.level_order: Order of the level in the simulation.
     * - simType: Type of the simulation.
     * - document: Documents related to the simulation.
     * - hotLinks: Additional resources or hot-links for the simulation.
     * - level.screenRecording: Indicates if screen recording is enabled for the level.
     * - captivate_learning_video_url: URL of the learning video.
     * - captivate_learning_url: URL of the captivate learning resource.
     */
    const resumeLevel = () => {
        fetchUserResponse({
            sim_level_id: level.level_id,
            sim_id: simId,
            mode: mode,
            resumeLevel: true,
            assignment_id: assignmentData ? assignmentData.assignment_id : undefined,
        });

        if (assignmentData && assignmentData.assignment_id && assignmentData.status && assignmentData.status.toLowerCase() !== 'completed') {
            const data = {
                _id: assignmentData.assignment_id,
                status: "IN PROGRESS",
            };
            addAssignmentStatus(data);
        }

        history.push({
            pathname: "/sims/takesim",
            search: assignmentData?.assignment_id
                ? `?levelId=${level.level_id}&assignment_id=${
                    assignmentData.assignment_id
                }${
                    assignmentData?.reviewer ? "&reviewer=true" : "&reviewer=false"
                }&continueSimLevel=true&activeIndex=${activeClip}&order=${level.level_order}`
                : `?levelId=${level.level_id}${
                    assignmentData?.reviewer ? "&reviewer=true" : "&reviewer=false"
                }&continueSimLevel=true&activeIndex=${activeClip}&order=${level.level_order}`,
            state: {
                viewResult: false,
                assignment: assignmentData ? assignmentData : {},
                type: simType,
                documents: document,
                hotLinks: hotLinks,
                screenRecording: level.screenRecording,
                captivate_learning_video_url: captivate_learning_video_url,
                captivate_learning_url: captivate_learning_url,
            },
        });
    };

    /**
     * Generates a link component to view simulation results based on the provided parameters.
     *
     * @param {Object} level - The level details.
     * @param {number} level.level_id - Unique identifier for the level.
     * @param {number} level.level_order - The order of the level.
     *
     * @param {string} simType - Type of simulation that is being referenced.
     *
     * @param {Object} [assignmentData] - Optional data related to the assignment.
     * @param {number} [assignmentData.assignment_id] - Unique identifier for the assignment.
     * @param {number} [assignmentData.sim_id] - Simulation identifier tied to the assignment.
     * @param {number} [assignmentData.assigned_to_id] - User ID to whom the assignment is directed.
     * @param {number} [assignmentData.agent_user_id] - Alternate user ID for the agent.
     * @param {boolean} [assignmentData.reviewer] - Indicates if the user is a reviewer.
     *
     * @returns {Object} A styled link component directing the user to the "View Results" page with the appropriate URL parameters.
     */
    const showViewResultsButton = (level, simType, assignmentData) => {
        let params = [
            `levelId=${level.level_id}`,
            `order=${level.level_order}`,
        ];
        if (assignmentData?.assignment_id) {
            params.push(`assignment_id=${assignmentData.assignment_id}`);
            params.push(`sim_id=${assignmentData.sim_id}`);
            params.push(`user_id=${assignmentData.assigned_to_id ?? assignmentData.agent_user_id}`);
        } else {
            if (assignmentData?.reviewer) {
                params.push(`reviewer=true`);
            }
        }
        return (
            <StyledLink
                to={{
                    pathname: "/sims/takesim",
                    search: params.join('&'),
                    type: simType,
                    state: {
                        viewResult: true,
                        assignment: assignmentData,
                    },
                }}
            >
                <u>View Results</u>
            </StyledLink>
        );
    };

    /**
     * Generates a component displaying the completion statistics and status for a given level.
     *
     * @param {object} level - The level data object containing details about attempts and completions.
     * @returns {JSX.Element} A JSX element representing the completion status for the given level, including
     *                        information such as the number of attempts, completions, and associated recordings.
     */
    const levelCompletionStats = (level) => {
        console.log('Level = %o', level);

        let attempts = level.total_attempts,
            completed = level.completed_attempts,
            hasResult = userResultExist || (attempts && completed);

        if (!userResultExist) {
            return (
                <div className={classes.flexItem2}>
                    <div className={classes.status}>
                        <div>Not started yet...</div>
                    </div>
                </div>
            );
        }

        if (!completed) {
            if (attempts) {
                return (
                    <div className={classes.flexItem2}>
                        <div className={classes.status}>
                            <div>
                                Completed {level.completed_attempts} times in{" "}{level.total_attempts} attempts
                            </div>
                        </div>
                    </div>
                );
            }

            return (
                <div className={classes.flexItem2}>
                    <div className={classes.status}>
                        <div>Not started yet...</div>
                    </div>
                </div>
            );
        }

        if (attempts) {
            return (
                <div className={classes.flexItem2}>
                    <div className={classes.status}>
                        <div>
                            Completed {level.completed_attempts} times in{" "}{level.total_attempts} attempts
                            <br/>
                            {level?.screen_recordings?.length ? (
                                <Tooltip
                                    title={`User has ${level.screen_recordings.length} screen recordings for this level`}
                                    placement="top"
                                >
                                    <div style={{
                                        marginRight: '1em',
                                        verticalAlign: 'middle',
                                        display: 'inline-block',
                                    }}>
                                        <VideocamIcon fontSize="small"/>
                                    </div>
                                </Tooltip>
                            ) : (<></>)}
                            {hasResult ?
                                showViewResultsButton(level, simType, assignmentData) :
                                (<></>)
                            }
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className={classes.flexItem2}>
                <div className={classes.status}>
                    {hasResult ? (<p>No results found</p>) : (<p>Checking for results...</p>)}
                </div>
            </div>
        );
    };

    /**
     * Determines and renders the available play modes for a given level
     * using Popper and displays actionable buttons for each mode.
     *
     * @function levelPlayModes
     * @param {Object} level - The level configuration object which includes information about the play modes.
     * @returns {JSX.Element} A Popper component rendering buttons for practice and autoplay modes with corresponding tooltips.
     */
    const levelPlayModes = (level) => {
        let practiceMode = (level.play_mode & PLAYMODE_PRACTICE) === PLAYMODE_PRACTICE,
            autoplayMode = (level.play_mode & PLAYMODE_AUTOPLAY) === PLAYMODE_AUTOPLAY;

        return (
            <Popper
                id={id}
                open={open}
                anchorEl={anchorEl}
                placement="left"
                className={classes.popper}
                disablePortal={false}
                onMouseLeave={handlePopoverClose}
                modifiers={{
                    flip: {
                        enabled: true,
                    },
                    preventOverflow: {
                        enabled: true,
                        boundariesElement: "scrollParent",
                    },
                    arrow: {
                        enabled: true,
                        element: arrowRef,
                    },
                }}
            >
                <div
                    data-popper-arrow
                    className={classes.rightTriangle}
                    ref={handleArrowRef}
                />
                <Paper elevation={2} className={classes.popperPaper}>
                    {
                        practiceMode ?
                            <div>
                                <Tooltip
                                    placement={'top-start'}
                                    title="Start here and you'll be able to control the pace of the call with pause, forward and back buttons.
                                        You'll also have the option to listen to and re-record your audio !"
                                >
                                    <Button
                                        variant="contained"
                                        className={classes.practiceBtn}
                                        disableElevation
                                        onClick={startPractice}
                                    >
                                        Start in Practice mode
                                    </Button>
                                </Tooltip>
                            </div>
                            : null
                    }
                    {
                        autoplayMode ?
                            <div>
                                <Tooltip
                                    placement={'bottom-start'}
                                    title="Start here and the call automatically plays through from start to finish.This means no stopping and
                                        no re-recording your audio.It's as close as it gets to real call experience !"
                                >
                                    <Button
                                        variant="contained"
                                        className={classes.autoplayBtn}
                                        disableElevation
                                        onClick={startAutoPlay}
                                    >
                                        Start in Autoplay mode
                                    </Button>
                                </Tooltip>
                            </div>
                            : null
                    }
                </Paper>
            </Popper>
        );
    };

    //debug(">>>> LEVEL: %o", level);

    return (
        <Box
            display="flex"
            justifyContent="space-around"
            color="default"
            mb={1}
            borderRadius={16}
            className={classes.root}
            onMouseLeave={handlePopoverClose}
        >
            <Modal
                open={openModal}
                onClose={handleClose}
                aria-labelledby="simple-modal-title"
                aria-describedby="simple-modal-description"
            >
                <div className={classes.paper}>
                    {(level.screenRecording && level.screen_recordings?.length) ? (
                        <>
                            <p>You can not resume levels with partial Screen Sharing recordings.</p>
                            <button
                                className={classes.startBtn}
                                style={{marginRight: "1rem"}}
                                onClick={() => startOver()}
                            >
                                START OVER
                            </button>
                        </>
                    ) : (
                        <>
                            <p>You already started this level. Do you want to resume?</p>
                            <button
                                className={classes.startBtn}
                                style={{marginRight: "1rem"}}
                                onClick={() => startOver()}
                            >
                                START OVER
                            </button>
                            <button className={classes.startBtn} onClick={resumeLevel}>
                                RESUME
                            </button>
                        </>
                    )}
                </div>
            </Modal>
            <Box display="flex" alignItems="center" className={classes.flexItem1}>
                {level.pass_fail_details &&
                    <Tooltip title={'This level is configured as the PASS/FAIL level of the SIM'}
                             placement="top"
                    >
                        <div style={{
                            marginRight: '1em',
                            verticalAlign: 'middle',
                            display: 'inline-block',
                        }}>
                            <AssignmentTurnedInIcon fontSize="small" className={classes.checkBoxFilled}/>
                        </div>
                    </Tooltip>
                }
                {
                    level.completed_attempts > 0 &&
                    <div className={classes.flex2item1}>
                        <Checkbox
                            className={classes.checkBox}
                            icon={<CircleUnchecked/>}
                            checkedIcon={<CircleCheckedFilled className={classes.checkBoxFilled}/>}
                            checked={level.completed_attempts > 0}
                        />
                    </div>
                }
                {level.screenRecording ? (
                    <Tooltip title={'A level in this SIM allows screen share recordings'}
                             placement="top"
                    >
                        <div style={{
                            marginRight: '1em',
                            verticalAlign: 'middle',
                            display: 'inline-block',
                        }}>
                            <ScreenShareIcon fontSize="small"/>
                        </div>
                    </Tooltip>
                ) : (
                    <></>
                )}
                <Tooltip
                    title={levelTitles[level?.level_order] ?? ''}
                    placement="top"
                >
                    <div className={level.completed_attempts > 0 ? '' : classes.levelDiv}>
                        <div className={classes.textBold}>
                            LEVEL&nbsp;
                            {index + 1}
                        </div>
                        <div>{level.level_name}</div>
                    </div>
                </Tooltip>
            </Box>
            <div className={classes.flexItem3}>
                {simType === "hiring" ? (
                    <Tooltip
                        title={currentAssignmentStatus?.status === "PENDING REVIEW" ? "Locked" : ""}
                        placement="top"
                    >
                    <span>
                        <Button
                            variant="contained"
                            className={classes.startBtn}
                            disableElevation
                            color="primary"
                            size="small"
                            aria-describedby={id}
                            onClick={startAutoPlay}
                            disabled={currentAssignmentStatus?.status === "PENDING REVIEW"}
                        >
                            START
                        </Button>
                    </span>
                    </Tooltip>
                ) : (
                    <Tooltip
                        title={currentAssignmentStatus?.status === "PENDING REVIEW" ? "Locked" : ""}
                        placement="top"
                    >
                    <span>
                        <Button
                            color="primary"
                            variant="contained"
                            disableElevation
                            className={classes.startBtn}
                            size="small"
                            onClick={handleClick}
                            aria-describedby={id}
                            disabled={currentAssignmentStatus?.status === "PENDING REVIEW"}
                        >
                            START
                        </Button>
                    </span>
                    </Tooltip>
                )}
            </div>
            <Divider orientation="vertical" flexItem/>

            {levelCompletionStats(level)}
            {levelPlayModes(level)}
        </Box>
    );
};

/**
 * PropTypes definition for the Levels component.
 *
 * This object defines the expected types for the props
 * that can be passed to the Levels component.
 *
 * By leveraging PropTypes, the Levels component can
 * ensure proper usage and catch potential type-related issues
 * at runtime during development.
 */
Levels.propTypes = {
    level: PropTypes.shape({
        level_order: PropTypes.number,
        level_name: PropTypes.string,
        total_attempts: PropTypes.number,
        completed_attempts: PropTypes.number,
        level_id: PropTypes.string,
        screenRecording: PropTypes.bool,
        screen_recordings: PropTypes.array,
        play_mode: PropTypes.number,
        pass_fail_details: PropTypes.object,
    }),
    index: PropTypes.number,
    simId: PropTypes.string,
    type: PropTypes.string,
    fetchUserResponse: PropTypes.func,
    assignmentData: PropTypes.object,
    addAssignmentStatus: PropTypes.func,
    currentAssignmentStatus: PropTypes.object,
    document: PropTypes.array,
    hotLinks: PropTypes.array,
    captivate_learning_url: PropTypes.string,
    captivate_learning_video_url: PropTypes.string,
    elapsedTime: PropTypes.number,
    caseValuesArray: PropTypes.object,
    userResponse: PropTypes.object,
    userFinalScoreResponse: PropTypes.func,
};

/**
 * Maps the relevant state properties from the Redux store to the component's props.
 *
 * @param {Object} state - The current Redux state.
 * @returns {Object} An object containing the mapped state properties:
 * - `elapsedTime`: The elapsed time from the `sim` slice of the Redux state.
 * - `caseValuesArray`: An array of case values from the `sim` slice of the Redux state.
 * - `userResponse`: The user's response from the `sim` slice of the Redux state.
 */
const mapStateToProps = (state) => ({
    elapsedTime: state.sim.elapsedTime,
    caseValuesArray: state.sim.caseValuesArray,
    userResponse: state.sim.userResponse,
});

/**
 * An object that maps dispatchable action creators to component props.
 *
 * @typedef {Object} mapDispatchToProps
 * @property {Function} fetchUserResponse - Dispatches an action to retrieve user response data.
 * @property {Function} addAssignmentStatus - Dispatches an action to update the status of an assignment.
 * @property {Function} userFinalScoreResponse - Dispatches an action to retrieve the final score of a user.
 */
const mapDispatchToProps = {
    fetchUserResponse,
    addAssignmentStatus,
    userFinalScoreResponse,
};

export default connect(mapStateToProps, mapDispatchToProps)(Levels);
